import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Building2, Plus, Trash2, Mail, Users, Package, Check, X } from 'lucide-react';
import { useSession } from "@/lib/auth-client";
import { useUser } from "@/context/user-provider";
import { organizationService } from "@/services/organization-service";
import type { Invitation, Organization } from 'better-auth/plugins/organization';
import OrganizationAssetsList from '@/components/features/assets/organization-assets-list';
import { useLocation, useNavigate } from 'react-router-dom';

interface ResponseMember {
  id: string;
  userId: string;
  organizationId: string;
  role: string;
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null | undefined;
  };
  createdAt: string | Date;
}

interface IncomingInvitation {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  organizationId: string;
  organizationName: string;
  inviterId: string;
  inviterName: string;
}

export function OrganizationManagement() {
  const { data: session } = useSession();
  const { 
    organizations, 
    activeOrganization, 
    switchOrganization, 
    refreshOrganizations,
    isOrgLoading: userProviderIsLoading 
  } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Organization state
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  
  // Members state
  const [members, setMembers] = useState<ResponseMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [incomingInvitations, setIncomingInvitations] = useState<IncomingInvitation[]>([]);
  
  // Invitation state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('member');

  // Routing hooks (must be at the top level of the component)
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine default tab based on URL path
  const getTabFromLocation = () => {
    const path = location.pathname;
    if (path.includes('/members')) return 'members';
    if (path.includes('/assets')) return 'assets';
    if (path.includes('/invitations')) return 'invitations';
    return 'organizations'; // default
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromLocation());
  
  // Update activeTab when location changes
  useEffect(() => {
    const tab = getTabFromLocation();
    setActiveTab(tab);
  }, [location.pathname]);

  const activeOrganizationId = activeOrganization?.organizationId || null;

  useEffect(() => {
    // Initialize with current user state
    if (!userProviderIsLoading) {
      setLoading(false);
      // If we have an active organization, fetch members for it
      if (activeOrganization) {
        fetchMembers(activeOrganization.organizationId);
      }
      // Fetch incoming invitations
      fetchIncomingInvitations();
    }
  }, [userProviderIsLoading, activeOrganization]);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    let path = '/console/organizations';
    
    if (value === 'members' || value === 'assets' || value === 'invitations') {
      path += `/${value}`;
    }
    
    navigate(path);
  };

  const fetchMembers = async (orgId: string) => {
    try {
      setError(null);

      // Use organization service
      const members = await organizationService.listMembers(orgId);
      setMembers(members);

      const invitations = await organizationService.listInvitations(orgId);
      setInvitations(invitations);

    } catch (err: any) {
      console.error('Error fetching members/invitations:', err);
      setError(err.message || 'Failed to load members and invitations');
    }
  };

  const fetchIncomingInvitations = async () => {
    try {
      const invitations = await organizationService.getIncomingInvitations();
      setIncomingInvitations(invitations);
    } catch (err: any) {
      console.error('Error fetching incoming invitations:', err);
    }
  };

  const handleCreateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newOrgName.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      // Use organization service
      await organizationService.create(newOrgName.trim(), newOrgSlug);

      setSuccess('Organization created successfully');
      setNewOrgName('');
      setNewOrgSlug('');

      // Refresh the list from the user provider
      await refreshOrganizations();
    } catch (err: any) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization');
    }
  };

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      // Use current active organization from the user provider
      if (!activeOrganizationId) {
        throw new Error('No active organization selected');
      }

      // Use organization service
      await organizationService.inviteMember(inviteEmail, inviteRole, activeOrganizationId);

      setSuccess('Invitation sent successfully');
      setInviteEmail('');

      // Refresh the current organization's members and invitations
      await fetchMembers(activeOrganizationId);
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      setError(err.message || 'Failed to send invitation');
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      // Use organization service
      await organizationService.cancelInvitation(invitationId);

      setSuccess('Invitation cancelled successfully');

      // Refresh the current organization's invitations
      if (activeOrganizationId) {
        await fetchMembers(activeOrganizationId);
      }
    } catch (err: any) {
      console.error('Error cancelling invitation:', err);
      setError(err.message || 'Failed to cancel invitation');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      // Use organization service
      await organizationService.removeMember(memberId);

      setSuccess('Member removed successfully');

      // Refresh the current organization's members
      if (activeOrganizationId) {
        await fetchMembers(activeOrganizationId);
      }
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      // Use organization service
      await organizationService.acceptInvitation(invitationId);

      setSuccess('Invitation accepted successfully');

      // Refresh incoming invitations and organizations
      await fetchIncomingInvitations();
      await refreshOrganizations();
    } catch (err: any) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      // Use organization service
      await organizationService.declineInvitation(invitationId);

      setSuccess('Invitation declined successfully');

      // Refresh incoming invitations
      await fetchIncomingInvitations();
    } catch (err: any) {
      console.error('Error declining invitation:', err);
      setError(err.message || 'Failed to decline invitation');
    }
  };

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      await switchOrganization(orgId);
      setSuccess('Organization switched successfully');
      
      // Refresh members and invitations for the new organization
      await fetchMembers(orgId);
    } catch (err) {
      console.error('Error switching organization:', err);
      setError('Failed to switch organization');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Organization Management</h1>
        <p className="text-muted-foreground">Manage your organizations, members, and invitations</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-6">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="organizations" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="members" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Members
          </TabsTrigger>
          <TabsTrigger value="assets" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Assets
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Invitations
          </TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Your Organizations
              </CardTitle>
              <CardDescription>Create and manage your organizations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Create Organization Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Create New Organization</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateOrganization} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="orgName">Organization Name</Label>
                        <Input
                          id="orgName"
                          value={newOrgName}
                          onChange={(e) => setNewOrgName(e.target.value)}
                          placeholder="e.g., My Company, My Project"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="orgSlug">Organization Slug (Optional)</Label>
                        <Input
                          id="orgSlug"
                          value={newOrgSlug}
                          onChange={(e) => setNewOrgSlug(e.target.value)}
                          placeholder="e.g., my-company, my-project"
                        />
                        <p className="text-sm text-muted-foreground">
                          Used in URLs. If empty, will be generated from the name.
                        </p>
                      </div>
                      
                      <Button type="submit" className="flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Create Organization
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                
                {/* Organizations List */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Your Organizations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {organizations.length === 0 ? (
                      <p className="text-muted-foreground">No organizations created yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {organizations.map((org) => {
                          const isActive = activeOrganizationId === org.id;
                          
                          return (
                            <div 
                              key={org.id} 
                              className="flex items-center justify-between p-3 border rounded-md"
                            >
                              <div>
                                <div className="font-medium">{org.name}</div>
                                <div className="text-sm text-muted-foreground">
                                  Slug: {org.slug} | Created: {new Date(org.createdAt).toLocaleDateString()}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSwitchOrganization(org.id)}
                                  disabled={isActive}
                                >
                                  {isActive ? 'Active' : 'Switch'}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Organization Members
              </CardTitle>
              <CardDescription>Manage members in your organization</CardDescription>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-1">No Members</h3>
                  <p className="text-muted-foreground">
                    This organization has no members yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {members.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center justify-between p-4 border rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <div>
                        <div className="font-medium">{member.user.name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">
                          {member.user.email} | Role: {member.role}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {member.role === 'owner' && (
                          <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded">
                            Owner
                          </span>
                        )}
                        {member.role === 'admin' && (
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            Admin
                          </span>
                        )}
                        {member.role === 'member' && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                            Member
                          </span>
                        )}
                        
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Organization Assets
              </CardTitle>
              <CardDescription>Assets shared with your organization</CardDescription>
            </CardHeader>
            <CardContent>
              {activeOrganizationId ? (
                <OrganizationAssetsList 
                  orgId={activeOrganizationId} 
                  title="Organization Assets" 
                />
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-1">No Active Organization</h3>
                  <p className="text-muted-foreground">
                    Please select an organization to view its assets.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab - Contains both outgoing and incoming invitations */}
        <TabsContent value="invitations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Invitations
              </CardTitle>
              <CardDescription>Manage organization invitations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {/* Invite Member Form */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Send Invitation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleInviteMember} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="inviteEmail">Email Address</Label>
                        <Input
                          id="inviteEmail"
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="e.g., team-member@example.com"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="inviteRole">Role</Label>
                        <select
                          id="inviteRole"
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="w-full rounded-md border border-input bg-background px-3 py-2"
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      
                      <Button type="submit" className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Send Invitation
                      </Button>
                    </form>
                  </CardContent>
                </Card>
                
                {/* Outgoing Invitations (Sent Invitations) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sent Invitations</CardTitle>
                    {activeOrganization?.role && 
                      activeOrganization.role !== 'owner' && 
                      activeOrganization.role !== 'admin' && (
                      <CardDescription className="text-xs text-muted-foreground">
                        Note: Only organization owners and admins can cancel invitations
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    {invitations.length === 0 ? (
                      <p className="text-muted-foreground">No sent invitations.</p>
                    ) : (
                      <div className="space-y-3">
                        {invitations.map((invitation) => (
                          <div 
                            key={invitation.id} 
                            className="flex items-center justify-between p-3 border rounded-md"
                          >
                            <div>
                              <div className="font-medium">{invitation.email}</div>
                              <div className="text-sm text-muted-foreground">
                                Role: {invitation.role} | Expires: {invitation.expiresAt ? new Date(invitation.expiresAt).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {invitation.status === 'pending' && activeOrganization?.role && 
                                (activeOrganization.role === 'owner' || activeOrganization.role === 'admin') && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleCancelInvitation(invitation.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                              {invitation.status !== 'pending' && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  invitation.status === 'accepted' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Incoming Invitations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Incoming Invitations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {incomingInvitations.length === 0 ? (
                      <p className="text-muted-foreground">No incoming invitations.</p>
                    ) : (
                      <div className="space-y-3">
                        {incomingInvitations.map((invitation) => (
                          <div 
                            key={invitation.id} 
                            className="flex items-center justify-between p-3 border rounded-md"
                          >
                            <div>
                              <div className="font-medium">From: {invitation.inviterName}</div>
                              <div className="text-sm text-muted-foreground">
                                Organization: {invitation.organizationName} | Role: {invitation.role} | Expires: {new Date(invitation.expiresAt).toLocaleDateString()}
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleAcceptInvitation(invitation.id)}
                                className="flex items-center gap-1"
                              >
                                <Check className="w-4 h-4" />
                                Accept
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeclineInvitation(invitation.id)}
                                className="flex items-center gap-1"
                              >
                                <X className="w-4 h-4" />
                                Decline
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}